/**
 * DOCUMENTATION:
 * Component: UiUxAccessibility
 * Purpose: Educational page detailing Jakob Nielsen's Usability Heuristics and Web Accessibility (a11y) implementation.
 * * * Note for Junior Developers:
 * - Accessibility is not a "nice-to-have" feature to be added at the end of a project; it is a legal and ethical requirement.
 * - Golden Rule of ARIA: No ARIA is better than bad ARIA. Always use native Semantic HTML (<nav>, <button>) before resorting to role="" attributes.
 * - Never remove the CSS outline from focus states without providing a highly visible custom replacement. Keyboard users rely on it!
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1.5fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function UiUxAccessibility() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Core Usability and Accessibility Concepts
    const a11yConcepts = useMemo(() => [
        { concept: "Visibility of System Status", category: "Heuristic", description: "Always keep users informed about what is going on. (e.g., loading spinners, success toast notifications)." },
        { concept: "Error Prevention", category: "Heuristic", description: "Even better than good error messages is a careful design which prevents a problem from occurring in the first place (e.g., disabling the submit button until the form is valid)." },
        { concept: "Keyboard Navigation", category: "Accessibility", description: "Every interactive element MUST be reachable using only the 'Tab' key, and usable using 'Enter' or 'Space'. Look at your `:focus-visible` states." },
        { concept: "WAI-ARIA Attributes", category: "Accessibility", description: "Accessible Rich Internet Applications. HTML attributes (aria-hidden, aria-expanded, aria-live) that provide context strictly to screen reading software." },
    ], []);

    // Coding examples demonstrating how UX heuristics translate to code
    const codingModules = useMemo(() => [
        {
            title: "1. Keyboard Navigation & Focus States",
            code: `/* ❌ BAD: Removing the default outline traps keyboard users */\n.btn-bad:focus {\n  outline: none;\n}\n\n/* ✅ GOOD: Replacing the default outline with a highly visible custom ring */\n.btn-good {\n  background-color: #100720;\n  color: white;\n}\n\n/* :focus-visible only applies when using a keyboard, not when clicking with a mouse */\n.btn-good:focus-visible {\n  outline: none;\n  box-shadow: 0 0 0 4px #100720, 0 0 0 6px goldenrod;\n  transition: box-shadow 0.2s ease;\n}`,
            desc: "Never use `outline: none;` without providing a `box-shadow` replacement. By using `:focus-visible` instead of just `:focus`, you provide focus rings for keyboard users without adding ugly borders when mouse users click the button."
        },
        {
            title: "2. Visibility of System Status (ARIA Live Regions)",
            code: `\n<div class="error-msg" id="login-error">\n  Incorrect password.\n</div>\n\n\n<div \n  class="error-msg" \n  id="login-error"\n  aria-live="assertive" \n  role="alert"\n>\n  Incorrect password. Please try again.\n</div>`,
            desc: "In React and modern Single Page Applications (SPAs), pages don't reload. If an error message pops up visually, a blind user has no idea it happened. `aria-live` forces the screen reader to read the newly injected text immediately."
        },
        {
            title: "3. Semantic HTML vs ARIA Roles",
            code: `\n<div class="btn" onclick="submitForm()">Submit</div>\n\n\n<div \n  class="btn" \n  role="button" \n  tabindex="0" \n  onKeyDown={(e) => e.key === 'Enter' && submitForm()}\n  onClick="submitForm()"\n>\n  Submit\n</div>\n\n\n<button type="submit" class="btn">Submit</button>`,
            desc: "The golden rule of ARIA is: Do not use ARIA if a native HTML element exists. A `<button>` inherently knows how to handle focus and keyboard events. Forcing a `<div>` to do it requires extra JavaScript and attributes."
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    HEURISTICS & ACCESSIBILITY
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Inclusive Engineering
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            Usability heuristics are broad rules of thumb for creating intuitive interfaces (like letting the user easily recover from an error). <strong>Accessibility (a11y)</strong> is the technical implementation of those rules to ensure users with visual, motor, or cognitive impairments can use your application just as effectively as anyone else. Writing accessible code is the absolute mark of a professional frontend engineer.
                        </Typography>

                        {/* ✅ Core Concepts Table */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Key Usability & A11y Standards</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Principle</div>
                                    <div>Category</div>
                                    <div>Implementation Description</div>
                                </ResponsiveDataRow>
                            )}
                            {a11yConcepts.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod", fontFamily: "'Fira Code', monospace" }}>{item.concept}</Box>
                                    <Box sx={{ fontSize: "0.95rem", fontWeight: 500 }}>{item.category}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/uiux-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
